<template>
	<div :ref="fieldRef" class="gallery-edit-block">
		<default-widget-field v-bind="defaultFieldProps" v-on="defaultFieldListeners" />
		<div>
			<div class="gallery" v-if="urls.length > 0">
				<div class="left-arrow" :class="{ disabled: offset <= 0 }" @click="moveCarousel(-1)">
					<GalleryLeftArrow />
				</div>
				<div class="carousel-window" :style="{ height: imgHeight + 60 + 'px' }">
					<div class="carousel" :style="{ left: -offset + '%' }">
						<div
							:key="url"
							class="image-display"
							v-for="(url, index) in urls"
							@click="toggleSelection(index)"
							:class="{ 'is-selected': userSelectionEnabled && (localValue === urls[index] || value === urls[index]) }"
						>
							<img :src="url" :style="{ height: imgHeight + 'px' }" />
						</div>
					</div>
				</div>
				<div class="right-arrow" :class="{ disabled: offset >= 100 }" @click="moveCarousel(1)">
					<GalleryRightArrow />
				</div>
			</div>

			<div class="empty-state" v-else>
				<GalleryEmptyState />
				<div class="empty-gallery-text">No images from previous step</div>
			</div>
		</div>
	</div>
</template>

<script>
import WidgetFieldMixin from './WidgetFieldMixin';
import DefaultWidgetField from './DefaultWidgetField';
import GalleryLeftArrow from '../assets/gallery-left-arrow.svg';
import GalleryEmptyState from '../assets/gallery-left-arrow.svg';
import GalleryRightArrow from '../assets/gallery-right-arrow.svg';
import ParamsService from '@/utils/ParamsService';

const getGallerySources = (object) => {
	// prettier-ignore
	const re = /(http[s]*:\/\/)([a-z\-_0-9/.]+)\.([a-z.]{2,3})\/([a-z0-9\-_/._~:?#[\]@!$&'()*+,;=%]*)([a-z0-9]+\.)(png|jpg|jpeg|bmp|gif|svg)/gi;
	const objectStr = JSON.stringify(object);
	const results = String(objectStr).match(re);
	return results ? [...new Set(results)] : [];
};

export default {
	name: 'GalleryField',
	components: {
		DefaultWidgetField,
		GalleryLeftArrow,
		GalleryRightArrow,
		GalleryEmptyState,
	},
	props: {
		previousSteps: { type: Object },
	},
	mixins: [WidgetFieldMixin],
	computed: {
		stepUUID() {
			return ParamsService.extractNodeUUIDFromValue(this.fieldOptions.step_id.value);
		},
		userSelectionEnabled() {
			return this.fieldOptions.user_selection.enabled;
		},
		urls() {
			if (!this.previousSteps) {
				return '';
			}
			const previousSteps = Object.keys(this.previousSteps)
				.filter((key) => key.includes(this.stepUUID))
				.reduce((obj, key) => {
					obj[key] = this.previousSteps[key];
					return obj;
				}, {});
			return getGallerySources(previousSteps);
		},
	},
	methods: {
		moveCarousel(amount) {
			this.offset = this.offset + amount * (100 / this.urls.length) * 2;
			this.offset = this.offset < 0 ? 0 : Math.min(100, this.offset);
		},
		toggleSelection(index) {
			if (!this.userSelectionEnabled) {
				return;
			}
			const selection = this.urls[index];
			this.value = this.value === selection ? null : selection;
			this.localValue = this.localValue === selection ? null : selection;
		},
	},
	data() {
		return {
			offset: 0,
			imgHeight: 100,
			localValue: null,
		};
	},
};
</script>

<style scoped lang="scss">
.gallery-edit-block {
	.gallery {
		display: flex;
		justify-content: space-between;
		align-items: center;

		.carousel-window {
			position: relative;
			display: flex;
			justify-content: space-between;
			align-items: center;
			width: 100%;
			overflow: hidden;

			.carousel {
				position: absolute;
				display: flex;
				justify-content: space-between;
				align-items: center;
				transition: 0.3s ease-in-out;

				.image-display {
					margin: 0 4px;
					padding: 3%;
					background-color: #eff2f9;
					border: 1px solid #d6ddeb;
					border-radius: 6px;
					transition: 0.5s;

					img {
						width: auto;
					}

					&:not(.is-selected) {
						margin: 1px 5px;
					}
					&.is-selected {
						margin: 0 4px; // prevent jump since border goes 1px --> 2px
						border: 2px solid #5858ec;
						background-color: #dfdffc;
					}
					&:hover {
						border-color: #7e7d7e;
						transition: 0.2s ease-in-out;
					}
				}
			}
		}

		.left-arrow,
		.right-arrow {
			&.disabled {
				opacity: 0.5;
			}
			&:hover {
				opacity: 0.7;
				cursor: pointer;
			}
		}
	}

	.empty-state {
		position: relative;
		display: flex;
		justify-self: center;
		align-items: center;

		svg {
			margin: auto;
			opacity: 0.5;
		}
		.empty-gallery-text {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			font-weight: bold;
		}
	}

	.gallery-preview-placeholder {
		max-width: 100%;
		overflow: hidden;
	}
}
</style>
